import { useEffect, useState } from "react";
import {
  LdsButton,
  useToastContext,
  LdsSearch,
  LdsModal,
  useLdsModal,
} from "@elilillyco/ux-lds-react";
import { useDispatch, useSelector } from "react-redux";

import {
  UPDATE_USER_LIST,
  FETCH_USER_LIST,
  FETCH_BRAND_LIST,
  FETCH_INCENTIVIZED_JOB,
} from "../../store/admin";

import CLoader from "../../components/shared/CLoader";
import UsersTable from "../../components/admin/UsersTable";
import infoToastConfig from "../../components/shared/Toast";
import exportExcelSheet from "../../utils/overview/exportExcelSheet";

const Users = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(FETCH_USER_LIST());
    dispatch(FETCH_BRAND_LIST());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loading } = useSelector(({ admin }) => admin);
  const { userTableList, Brands } = useSelector(({ admin }) => admin);
  const { uuid } = useSelector(({ user }) => user.user || {});

  const { addToast } = useToastContext();

  const [searchText, setsearchText] = useState("");
  const { isModalOpen: isIncentivizedModalOpen, setIsModalOpen: setIsIncentivizedModalOpen } = useLdsModal();

  const saveUserListForm = async (obj) => {
    if (Array.isArray(obj)) {
      obj.forEach((object) => {
        delete object["isSelected"];
        delete object["isVisible"];
      });
    } else {
      delete obj.isVisible;
      delete obj.isSelected;
    }
    const updateUserDetails = {
      users: Array.isArray(obj) ? obj : [obj],
      updated_by: uuid,
    };
    return await dispatch(UPDATE_USER_LIST(updateUserDetails));
  };

  const checkSuperAdmin = () => {
    if (sessionStorage.getItem("role") === "spclty_hub_super_admin") {
      return true;
    }
    return false;
  };

  const checkAdminAccess = () => {
    const role = sessionStorage.getItem("role");
    return role === "spclty_hub_admin" || role === "spclty_hub_super_admin";
  };

  const convertBrandsToString = (brands) => {
    if (brands.length === Brands.length || brands.includes("ALL")) {
      // If user has ALL brands, return all brand names from the Brands list
      return Brands.map(brand => brand.label || brand.value).join(", ");
    }
    return brands.join(", ");
  };

  const downloadUsersCSV = () => {
    try {
      // Apply search filter to get only visible users
      const filteredUsers = userTableList.filter((user) => {
        if (!searchText) return true; // If no search text, include all users
        
        return (
          user.email_id.toLowerCase().includes(searchText.toLowerCase()) ||
          user.first_name.toLowerCase().includes(searchText.toLowerCase()) ||
          user.last_name.toLowerCase().includes(searchText.toLowerCase()) ||
          user.brand.find((brand) => brand.toLowerCase().includes(searchText.toLowerCase()))
        );
      });

      // Prepare data for CSV export
      const csvData = filteredUsers.map((user) => ({
        "Full Name": `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        "Email": user.email_id || "",
        "Role": user.role || "",
        "Brands": convertBrandsToString(user.brand || []),
        "Status": user.active ? "Active" : "Inactive",
      }));

      // Generate filename with current date and search info
      const currentDate = new Date().toISOString().split('T')[0];
      // const searchSuffix = searchText ? `_filtered_${searchText.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
      const filename = `SHT_User_List_${currentDate}`;

      // Export to CSV
      exportExcelSheet(csvData, filename);
      
      const resultMessage = searchText 
        ? `${filteredUsers.length} filtered users exported successfully!`
        : `${filteredUsers.length} users exported successfully!`;
      
      addToast(infoToastConfig(resultMessage, "success"));
    } catch (error) {
      console.error("Error exporting users data:", error);
      addToast(infoToastConfig("Error exporting users data.", "error"));
    }
  };

  const listjobtrigger = async () => {
    setIsIncentivizedModalOpen(false);
    const { meta } = await dispatch(
      FETCH_INCENTIVIZED_JOB({
        created_by: uuid,
      })
    );
    if (meta.requestStatus === "fulfilled") {
      addToast(infoToastConfig("Lists Created Successfully.", "success"));
    }
  };

  return (
    <section className="section">
      <div className="wrapper overview-page">
        <div className="mt-3">
          <div className="row">
            <div className="col-12 mt-4 pl-4">
              <h2>Users</h2>
            </div>
          </div>
          <div className="row mb-4 pl-1">
            <div className="col-6">
              <LdsSearch
                onChange={setsearchText}
                value={searchText}
              ></LdsSearch>
            </div>
            <div className="col-6">
              {checkAdminAccess() && (
                <LdsButton
                  classes="button-icon-left blue-btn right mr-4"
                  onClick={downloadUsersCSV}
                >
                  Download as CSV
                </LdsButton>
              )}

              {checkSuperAdmin() && (
                <LdsButton
                  classes="button-icon-left blue-btn right mr-4"
                  onClick={() => setIsIncentivizedModalOpen(true)}
                >
                  Incentivized List
                </LdsButton>
              )}
            </div>
          </div>

          <LdsModal
            modalId="incentivized-list-confirm-modal"
            className="text-center"
            size="col-12 col-sm-8 col-md-6 col-lg-5"
            open={isIncentivizedModalOpen}
            setModalOpen={setIsIncentivizedModalOpen}
          >
            <div>
              <div className="textsize">
                <h2>Warning</h2>
                <p className="mt-3">
                  This will create new <strong>DRAFT</strong> salesforce lists for the next quarter
                  by rolling over all currently approved lists from the existing quarter.
                  Specialty codes will be sourced from the latest MLR list and responsibility
                  codes from the current salesforce list.
                </p>
                <p className="mt-2"><strong>Are you sure you want to proceed?</strong></p>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col">
                <div className="savecl">
                  <LdsButton
                    classes="button-icon-right blue-bdr-btn"
                    onClick={() => setIsIncentivizedModalOpen(false)}
                  >
                    No
                  </LdsButton>
                  <LdsButton
                    classes="button-icon-right blue-btn ml-4"
                    onClick={listjobtrigger}
                  >
                    Yes, Create Lists
                  </LdsButton>
                </div>
              </div>
            </div>
          </LdsModal>
          <div className="mt-3">
            {loading && <CLoader size={90} />}
            {!loading && (
              <UsersTable
                saveUserListForm={saveUserListForm}
                searchText={searchText}
                isProfile={false}
              ></UsersTable>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Users;
